import {
  AlertTriangle,
  Check,
  Clipboard,
  LoaderCircle,
  X,
} from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type PropsWithChildren,
  type ReactNode,
} from "react";

export function Button({
  children,
  className = "",
  variant = "primary",
  ...props
}: PropsWithChildren<
  ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: "danger" | "ghost" | "primary" | "secondary";
  }
>) {
  return (
    <button className={`button button--${variant} ${className}`} {...props}>
      {children}
    </button>
  );
}

export function StatusBadge({ status }: { status: string }) {
  const normalized = status.toUpperCase();
  const successStatuses = new Set(["ACTIVE", "APPROVED", "COMPLETED", "LINKED", "PROCESSED", "PUBLISHED", "READY", "STABLE"]);
  const warningStatuses = new Set(["BETA", "EXPIRED", "IGNORED", "NEEDS_ATTENTION", "PENDING", "PROCESSING", "RECEIVED"]);
  const infoStatuses = new Set(["ADMIN", "DEVELOPMENT", "MANAGER", "OWNER"]);
  const neutralStatuses = new Set(["ARCHIVED", "DRAFT", "REMOVED", "UNLINKED"]);
  const kind = successStatuses.has(normalized)
    ? "success"
    : warningStatuses.has(normalized)
      ? "warning"
      : infoStatuses.has(normalized)
        ? "info"
        : neutralStatuses.has(normalized)
          ? "neutral"
          : "danger";
  return (
    <span className={`status status--${kind}`}>
      <span aria-hidden="true" className="status__dot" />
      {normalized.replaceAll("_", " ")}
    </span>
  );
}

export function Capacity({ total, used }: { total: number; used: number }) {
  const percentage = total <= 0 ? 0 : Math.min(100, Math.round((used / total) * 100));
  return (
    <div className="capacity" aria-label={`${used} of ${total} used`}>
      <div className="capacity__value">
        <span>{used} / {total}</span>
        <span>{percentage}%</span>
      </div>
      <div className="capacity__track" aria-hidden="true">
        <span style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
}

export function CopyButton({ value, label = "Copy" }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1_800);
  }
  return (
    <button className="icon-action" onClick={() => void copy()} title={label} type="button">
      {copied ? <Check size={16} /> : <Clipboard size={16} />}
      <span className="sr-only">{copied ? "Copied" : label}</span>
    </button>
  );
}

export function LoadingState({ label = "Loading" }: { label?: string }) {
  return (
    <div className="loading-state" role="status">
      <LoaderCircle className="spin" size={22} />
      <span>{label}</span>
    </div>
  );
}

export function EmptyState({
  action,
  description,
  icon,
  title,
}: {
  action?: ReactNode;
  description: string;
  icon: ReactNode;
  title: string;
}) {
  return (
    <div className="empty-state">
      <div className="empty-state__icon">{icon}</div>
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  );
}

export function InlineNotice({
  children,
  kind = "info",
}: PropsWithChildren<{ kind?: "danger" | "info" | "success" | "warning" }>) {
  return (
    <div className={`notice notice--${kind}`} role={kind === "danger" ? "alert" : "status"}>
      {kind === "danger" || kind === "warning"
        ? <AlertTriangle size={18} aria-hidden="true" />
        : <Check size={18} aria-hidden="true" />}
      <div>{children}</div>
    </div>
  );
}

export function ConfirmDialog({
  busy = false,
  confirmDisabled = false,
  confirmLabel,
  description,
  onCancel,
  onConfirm,
  open,
  title,
  tone = "danger",
}: {
  busy?: boolean;
  confirmDisabled?: boolean;
  confirmLabel: string;
  description: ReactNode;
  onCancel(): void;
  onConfirm(): void;
  open: boolean;
  title: string;
  tone?: "danger" | "primary";
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  return (
    <dialog
      aria-labelledby="confirm-dialog-title"
      className="dialog"
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onCancel();
      }}
      ref={ref}
    >
      <div className="dialog__head">
        <h2 id="confirm-dialog-title">{title}</h2>
        <button className="icon-action" disabled={busy} onClick={onCancel} type="button">
          <X size={18} />
          <span className="sr-only">Close</span>
        </button>
      </div>
      <div className="dialog__body">{description}</div>
      <div className="dialog__actions">
        <Button disabled={busy} onClick={onCancel} variant="ghost">Cancel</Button>
        <Button disabled={busy || confirmDisabled} onClick={onConfirm} variant={tone}>
          {busy && <LoaderCircle className="spin" size={16} />}
          {confirmLabel}
        </Button>
      </div>
    </dialog>
  );
}

export function formatDate(value: string | null, options?: Intl.DateTimeFormatOptions) {
  if (!value) return "Lifetime";
  return new Intl.DateTimeFormat("en", options || {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

export function relativeTime(value: string) {
  const delta = new Date(value).getTime() - Date.now();
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ["day", 86_400_000],
    ["hour", 3_600_000],
    ["minute", 60_000],
  ];
  const formatter = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  for (const [unit, ms] of units) {
    if (Math.abs(delta) >= ms || unit === "minute") {
      return formatter.format(Math.round(delta / ms), unit);
    }
  }
  return "now";
}
